import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Loader2, CheckCircle, DollarSign, ShieldCheck, Save, Users, Mail, Search } from "lucide-react";
import AircallDialButton from "@/pages/workflow/AircallDialButton";
import { getPgContacts, postcodeAddresses, auPostcodeSuburbs, prefundingLoanSummary, checkPrefundingIdentity } from "@/lib/api";

// Live verdict for a Directors identity answer — graded server-side against the contract; the value on file is
// never sent to the browser, so the operator only ever sees match / no match.
const IDENTITY_TONE = {
  pass: { box: "bg-emerald-50 border-emerald-200", head: "text-emerald-800", sub: "text-emerald-700", label: "Matches the record on file" },
  fail: { box: "bg-rose-50 border-rose-300", head: "text-rose-800", sub: "text-rose-700", label: "Does NOT match the record on file — the call will FAIL" },
  pending: { box: "bg-slate-50 border-slate-200", head: "text-slate-800", sub: "text-slate-600", label: "Checking against the record on file…" },
};

function IdentityVerdict({ status, testId }) {
  if (!status) return null;
  const tone = IDENTITY_TONE[status.pass === true ? "pass" : status.pass === false ? "fail" : "pending"];
  return (
    <p className={`text-[11px] font-medium ${tone.head}`} data-testid={`${testId}-verdict`} data-verdict={status.pass === true ? "pass" : status.pass === false ? "fail" : "pending"}>
      {status.pass === false && status.note ? `✖ ${status.note} — the call will FAIL` : status.pass === true ? `✔ ${tone.label}` : tone.label}
    </p>
  );
}

// Postcode → address lookup answer (spot check) — ask for the postcode on
// the call, look it up, pick the address the customer states.
// UK: Ideal Postcodes lists every address at the postcode.
// AUS sole traders: the NCC s13 Business Purpose Declaration questions (appended server-side) carry a badge so the
// operator sees why the block is there; the hint says whether the declaration sits in the DocuSign envelope.
function BpdBadge({ question }) {
  if (!question.bpd) return null;
  return (
    <span className="text-[9px] bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-px ml-1.5 font-medium" data-testid={`bpd-badge-${question.id}`}>
      SOLE TRADER · NCC s13
    </span>
  );
}

// AUS: a postcode covers whole suburbs — confirm the suburb from the bundled
// Australia Post dataset, then type the street the customer states.
function AddressLookupAnswer({ value, onChange, disabled, testId, isAus, status }) {
  const [postcode, setPostcode] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [suburbs, setSuburbs] = useState([]);
  const [suburb, setSuburb] = useState("");
  const [street, setStreet] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const lookup = async () => {
    if (!postcode.trim()) return;
    setLoading(true); setError(""); setAddresses([]); setSuburbs([]); setSuburb("");
    try {
      if (isAus) {
        const r = await auPostcodeSuburbs(postcode.trim());
        if (r.ok && r.suburbs?.length) setSuburbs(r.suburbs);
        else setError(r.error || "No suburbs found for that postcode");
      } else {
        const r = await postcodeAddresses(postcode.trim());
        if (r.ok && r.addresses?.length) setAddresses(r.addresses);
        else setError(r.error || "No addresses found for that postcode");
      }
    } catch {
      setError("Address lookup failed — try again");
    } finally {
      setLoading(false);
    }
  };
  const composeAus = (streetVal, suburbLabel) => {
    if (!suburbLabel) { onChange(""); return; }
    onChange(streetVal.trim() ? `${streetVal.trim()}, ${suburbLabel}` : suburbLabel);
  };
  const lines = value ? value.split(", ") : [];
  return (
    <div className="space-y-1.5 max-w-md">
      <div className="flex gap-2">
        <Input
          value={postcode}
          onChange={(e) => setPostcode(isAus
            ? e.target.value.replace(/\D/g, "").slice(0, 4)
            : e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
          placeholder={isAus ? "Postcode e.g. 2000" : "Postcode e.g. SW1A 1AA"}
          className="h-8 text-sm bg-white w-44"
          disabled={disabled}
          data-testid={`${testId}-postcode`}
        />
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={lookup}
          disabled={disabled || loading || !postcode.trim()} data-testid={`${testId}-lookup-btn`}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
          Lookup
        </Button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {suburbs.length > 0 && (
        <Select value={suburb} onValueChange={(v) => { setSuburb(v); composeAus(street, v); }} disabled={disabled}>
          <SelectTrigger className="h-8 text-sm bg-white" data-testid={`${testId}-select`}>
            <SelectValue placeholder={`Select the suburb the customer states (${suburbs.length} found)…`} />
          </SelectTrigger>
          <SelectContent>
            {suburbs.map((s) => (
              <SelectItem key={s.label} value={s.label}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {isAus && suburb && (
        <Input
          value={street}
          onChange={(e) => { setStreet(e.target.value); composeAus(e.target.value, suburb); }}
          placeholder="Street address the customer states — e.g. 12 George St"
          className="h-8 text-sm bg-white"
          disabled={disabled}
          data-testid={`${testId}-street`}
        />
      )}
      {addresses.length > 0 && (
        <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="h-8 text-sm bg-white" data-testid={`${testId}-select`}>
            <SelectValue placeholder={`Select the address the customer states (${addresses.length} found)…`} />
          </SelectTrigger>
          <SelectContent>
            {addresses.map((a) => (
              <SelectItem key={a.id} value={a.fullAddress}>{a.fullAddress}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {value && (() => {
        const tone = IDENTITY_TONE[status?.pass === true ? "pass" : status?.pass === false ? "fail" : "pending"];
        return (
          <div className={`text-xs border rounded p-2 space-y-0.5 ${tone.box}`} data-testid={`${testId}-selected`}
            data-verdict={status?.pass === true ? "pass" : status?.pass === false ? "fail" : "pending"}>
            <p className={`font-medium ${tone.head}`}>{lines[0] || value}</p>
            {lines.length > 1 && <p className={tone.sub}>{lines.slice(1).join(", ")}</p>}
            <IdentityVerdict status={status} testId={testId} />
          </div>
        );
      })()}
    </div>
  );
}

// Big loan-figures box shown under the "confirm the loan amount…" question.
// Figures come from the signed contract (Docupilot loan_details) — stable,
// never payment × term rounding. Falls back to LoanDisk / application fields.
function LoanSummaryBox({ summary, curSym }) {
  const money = (v) => (v == null ? "—"
    : `${curSym}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const srcBadge = !summary ? null
    : summary.source === "contract"
      ? { label: "From signed contract (DocuPilot)", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" }
      : summary.source === "loandisk"
        ? { label: "From LoanDisk", cls: "bg-blue-100 text-blue-700 border-blue-200" }
        : { label: summary.estimated ? "From application — ESTIMATED, verify vs contract" : "From application",
            cls: "bg-amber-100 text-amber-800 border-amber-300" };
  const term = summary?.term_weeks ? `${summary.term_weeks} weeks`
    : summary?.num_payments ? `${summary.num_payments} payments` : "—";
  return (
    <div className="mt-2 p-4 border-2 border-blue-300 bg-blue-50 rounded-xl space-y-3" data-testid="prefunding-loan-summary">
      <div className="flex items-center justify-between flex-wrap gap-1">
        <p className="text-sm font-semibold text-blue-900 flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Loan figures — read these to the client
        </p>
        {srcBadge && (
          <span className={`text-[10px] border rounded px-1.5 py-0.5 font-medium ${srcBadge.cls}`} data-testid="loan-summary-source">
            {srcBadge.label}
          </span>
        )}
      </div>
      {!summary ? (
        <p className="text-xs text-muted-foreground">Loan figures unavailable — check the contract request / LoanDisk.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Loan amount</p>
              <p className="font-mono font-semibold text-base" data-testid="loan-summary-principal">{money(summary.principal)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Repayment</p>
              <p className="font-mono font-semibold text-base" data-testid="loan-summary-payment">
                {money(summary.payment_amount)}
                <span className="text-xs font-normal text-gray-500">/{(summary.payment_frequency || "week").replace("ly", "").toLowerCase()}</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Term</p>
              <p className="font-semibold text-base" data-testid="loan-summary-term">{term}</p>
            </div>
            {summary.admin_fee != null && (
              <div><p className="text-xs text-gray-500">Admin fee</p><p className="font-mono" data-testid="loan-summary-admin-fee">{money(summary.admin_fee)}</p></div>
            )}
            {summary.legal_fee != null && (
              <div><p className="text-xs text-gray-500">Legal fee</p><p className="font-mono" data-testid="loan-summary-legal-fee">{money(summary.legal_fee)}</p></div>
            )}
            {summary.factor_rate != null && (
              <div><p className="text-xs text-gray-500">Factor rate</p><p className="font-mono">{summary.factor_rate}</p></div>
            )}
          </div>
          <div className="pt-2 border-t border-blue-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-900">Total repayable</span>
            <span className="font-mono font-bold text-xl text-blue-900" data-testid="loan-summary-total">{money(summary.total_repayable)}</span>
          </div>
          {summary.first_repayment_date && (
            <p className="text-xs text-gray-600">
              First payment date (LoanDisk): <span className="font-medium">{summary.first_repayment_date}</span>
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Pop-out wizard for the Pre-Funding Call checklist — keeps the Credit Deals
// workflow page compact. All state lives in WorkflowChecklist (parent).
export default function PreFundingCallDialog({
  open, onOpenChange, application, questions, responses, notes, setNotes,
  recording, uploadingRecording, isFunded, processing, critical, setCritical,
  onCheckboxChange, onValueChange, onUploadRecording, onComplete,
  onSaveDraft, draftInfo,
}) {
  const requiredUnanswered = questions.filter((q) => q.required).some((q) => {
    const a = responses[q.id];
    return (q.input_type || "checkbox") === "checkbox"
      ? a !== true
      : (a == null || String(a).trim() === "");
  });

  // Critical info — everything required except the nominal amount (may be confirmed via email)
  const criticalIncomplete = !critical ||
    ["sort_code", "account_number", "payment_frequency", "payment_amount", "start_date"]
      .some((k) => !critical[k] || String(critical[k]).trim() === "");

  const setCriticalField = (key, value) => setCritical((prev) => ({ ...prev, [key]: value }));

  // PG (guarantor) contacts — fetched from the deal's latest contract request
  const [pgContacts, setPgContacts] = useState([]);
  const [loanSummary, setLoanSummary] = useState(null);
  const appKey = application?.id || application?.application_id;
  const isAus = application?.currency === "AUD" || ["AUS", "AU"].includes((application?.country || "").toUpperCase());
  const curSym = isAus ? "A$" : "£";
  useEffect(() => {
    if (!open || !appKey) return;
    getPgContacts(appKey)
      .then((r) => setPgContacts(r.guarantors || []))
      .catch(() => setPgContacts([]));
    prefundingLoanSummary(appKey)
      .then(setLoanSummary)
      .catch(() => setLoanSummary(null));
  }, [open, appKey]);

  // Directors identity block — which question holds which answer, graded live (debounced) against the contract.
  const identityKeyFor = (q) => {
    const txt = (q.text || "").toLowerCase();
    if (q.input_type === "address" || txt.includes("home address")) return "address";
    if (txt.includes("date of birth") && !q.spotcheck) return "dob";
    if (txt.includes("full name")) return "name";
    return null;
  };
  const identityAnswers = {};
  questions.forEach((q) => { const k = identityKeyFor(q); if (k && responses[q.id]) identityAnswers[k] = String(responses[q.id]); });
  const identityKey = JSON.stringify(identityAnswers);
  const [identityStatus, setIdentityStatus] = useState({});
  useEffect(() => {
    if (!open || !appKey) return;
    const answers = JSON.parse(identityKey);
    if (!Object.keys(answers).length) { setIdentityStatus({}); return; }
    setIdentityStatus((prev) => Object.fromEntries(Object.keys(answers).map((k) => [k, prev[k]?.entered === answers[k] ? prev[k] : { pass: null, entered: answers[k] }])));
    const t = setTimeout(() => {
      checkPrefundingIdentity(appKey, answers)
        .then((r) => setIdentityStatus(Object.fromEntries((r.results || []).map((x) => [x.key.replace("director_", ""), { pass: x.pass, note: x.note, entered: answers[x.key.replace("director_", "")] }]))))
        .catch(() => setIdentityStatus({}));
    }, 500);
    return () => clearTimeout(t);
  }, [open, appKey, identityKey]);
  const identityFailed = Object.values(identityStatus).some((s) => s?.pass === false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="prefunding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            Pre-Funding Call — {application.application_id || application.id}
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-normal">
              {application.is_topup ? "Top-Up" : "New Deal"}
            </span>
            <AircallDialButton
              application={application}
              presetNumber={application?.loandisk_loan_data?.borrower_mobile
                || application?.customer_phone || application?.mobile
                || pgContacts.find((g) => g.telephone)?.telephone || ""}
            />
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Complete this checklist during your call with the customer before funding.
          </p>
        </DialogHeader>

        {draftInfo && (
          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2" data-testid="prefunding-draft-banner">
            <Save className="w-4 h-4 shrink-0" />
            <span>
              Resumed from a saved draft — saved {draftInfo.saved_at ? new Date(draftInfo.saved_at).toLocaleString("en-GB") : ""}
              {draftInfo.saved_by ? ` by ${draftInfo.saved_by}` : ""}. Closing this dialog saves your progress automatically.
            </span>
          </div>
        )}

        {/* Guarantor (PG) contacts — from the deal's contract request */}
        {pgContacts.length > 0 && (
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="prefunding-pg-contacts">
            <p className="text-sm font-medium text-indigo-800 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Guarantor (PG) Contacts:
            </p>
            <div className="space-y-1.5">
              {pgContacts.map((g, idx) => (
                <div key={idx} className="flex items-center justify-between gap-2 text-sm bg-white/60 p-2 rounded" data-testid={`prefunding-pg-contact-${idx}`}>
                  <span className="font-medium text-indigo-900 truncate">{g.name || "—"}</span>
                  <span className="flex items-center gap-3 shrink-0">
                    {g.telephone ? (
                      <span className="font-mono text-xs text-indigo-700">{g.telephone}</span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">no phone</span>
                    )}
                    {g.email ? (
                      <a
                        href={`mailto:${g.email}`}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        data-testid={`prefunding-pg-email-${idx}`}
                      >
                        <Mail className="w-3 h-3" />
                        {g.email}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 italic">no email</span>
                    )}
                    {g.telephone && (
                      <AircallDialButton
                        application={application}
                        presetNumber={g.telephone}
                        contactLabel={`Call ${(g.name || "PG").split(" ")[0]}`}
                        testId={`pg-dial-btn-${idx}`}
                      />
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consolidation lenders (refi deals) */}
        {application.consolidation_lenders?.length > 0 && (
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Consolidation Payments (Lenders to Pay Off):
            </p>
            <div className="space-y-2">
              {application.consolidation_lenders.map((lender, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm bg-white/60 p-2 rounded">
                  <div>
                    <span className="font-medium text-purple-900">{lender.lender_name}</span>
                    <span className="text-purple-600 text-xs ml-2">
                      ({lender.sort_code} / ****{lender.account_number?.slice(-4)})
                    </span>
                  </div>
                  <span className="font-mono font-medium text-purple-900">
                    {curSym}{parseFloat(lender.amount || 0).toLocaleString()}
                  </span>
                </div>
              ))}
              <div className="flex justify-between items-center text-sm font-medium pt-2 border-t border-purple-200">
                <span className="text-purple-800">Total Consolidation:</span>
                <span className="font-mono text-purple-900">
                  {curSym}{application.consolidation_lenders.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Checklist — dynamic input types (aligned to the Pre-Funding JotForm) */}
        <div className="space-y-2">
          {questions.map((question) => {
            const inputType = question.input_type || "checkbox";
            const value = responses[question.id];
            const isLoanAmountQ = (question.text || "").toLowerCase().includes("loan amount");
            return (
              <div key={question.id}
                className={`p-2 rounded hover:bg-blue-50 ${question.spotcheck ? "bg-violet-50/70 border border-violet-200" : question.bpd ? "bg-amber-50/70 border border-amber-300" : ""}`}
                data-testid={question.bpd ? `prefunding-bpd-${question.id}` : undefined}>
                {inputType === "checkbox" ? (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={value || false}
                      onChange={(e) => onCheckboxChange(question.id, e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isFunded}
                      data-testid={`prefunding-q-${question.order}`}
                    />
                    <span className="text-sm text-gray-700">
                      {question.text}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                      {question.spotcheck && (
                        <span className="text-[9px] bg-violet-100 text-violet-700 border border-violet-200 rounded px-1.5 py-px ml-1.5 font-medium" data-testid={`spotcheck-badge-${question.order}`}>ROTATING SPOT CHECK</span>
                      )}
                      <BpdBadge question={question} />
                    </span>
                  </label>
                ) : (
                  <div className="space-y-1.5">
                    <span className="text-sm text-gray-700 block">
                      {question.text}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                      {question.spotcheck && (
                        <span className="text-[9px] bg-violet-100 text-violet-700 border border-violet-200 rounded px-1.5 py-px ml-1.5 font-medium" data-testid={`spotcheck-badge-${question.order}`}>ROTATING SPOT CHECK</span>
                      )}
                      <BpdBadge question={question} />
                    </span>
                    {inputType === "address" ? (
                      <AddressLookupAnswer
                        value={value || ""}
                        onChange={(v) => onValueChange(question.id, v)}
                        disabled={isFunded}
                        testId={`prefunding-q-${question.order}`}
                        isAus={isAus}
                        status={identityStatus.address}
                      />
                    ) : inputType === "select" ? (
                      <Select
                        value={value || ""}
                        onValueChange={(v) => onValueChange(question.id, v)}
                        disabled={isFunded}
                      >
                        <SelectTrigger className="h-8 text-sm bg-white max-w-sm" data-testid={`prefunding-q-${question.order}`}>
                          <SelectValue placeholder="Select answer..." />
                        </SelectTrigger>
                        <SelectContent>
                          {(question.options || []).map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={inputType === "number" ? "number" : inputType === "date" ? "date" : "text"}
                        value={value || ""}
                        onChange={(e) => onValueChange(question.id, e.target.value)}
                        placeholder={question.placeholder || ""}
                        className="h-8 text-sm bg-white max-w-sm"
                        disabled={isFunded}
                        data-testid={`prefunding-q-${question.order}`}
                      />
                    )}
                    {identityKeyFor(question) && identityKeyFor(question) !== "address" && value && (
                      <IdentityVerdict status={identityStatus[identityKeyFor(question)]} testId={`prefunding-q-${question.order}`} />
                    )}
                    {question.expected_value && !identityKeyFor(question) && (
                      <p className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1 inline-block" data-testid={`prefunding-q-${question.order}-expected`}>
                        {question.expected_source || "Expected"}: <span className="font-semibold">{question.expected_value}</span>
                      </p>
                    )}
                  </div>
                )}
                {question.bpd && question.hint && (
                  <p className="mt-1 text-[11px] text-amber-800" data-testid={`prefunding-bpd-${question.id}-hint`}>{question.hint}</p>
                )}
                {isLoanAmountQ && <LoanSummaryBox summary={loanSummary} curSym={curSym} />}
              </div>
            );
          })}
        </div>

        {/* Critical information verification — graded pass/fail server-side */}
        <div className="p-3 border-2 border-blue-200 bg-blue-50/50 rounded-lg space-y-3" data-testid="prefunding-critical-section">
          <div>
            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              Critical Information Verification
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">GRADED PASS/FAIL</span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Enter EXACTLY what the customer states on the call. Answers are checked against the verified
              records — any mismatch FAILS the call and keeps disbursement locked until the full call is redone.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">{isAus ? "BSB" : "Bank sort code"} <span className="text-red-500">*</span></label>
              <Input
                value={critical?.sort_code || ""}
                onChange={(e) => setCriticalField("sort_code", e.target.value)}
                placeholder={isAus ? "e.g. 062-000" : "e.g. 20-00-00"}
                className="h-8 text-sm bg-white"
                disabled={isFunded}
                data-testid="critical-sort-code"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Account number <span className="text-red-500">*</span></label>
              <Input
                value={critical?.account_number || ""}
                onChange={(e) => setCriticalField("account_number", e.target.value)}
                placeholder={isAus ? "5–9 digits" : "8 digits"}
                maxLength={isAus ? 9 : 8}
                className="h-8 text-sm bg-white"
                disabled={isFunded}
                data-testid="critical-account-number"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Payment frequency <span className="text-red-500">*</span></label>
              <Select
                value={critical?.payment_frequency || ""}
                onValueChange={(v) => setCriticalField("payment_frequency", v)}
                disabled={isFunded}
              >
                <SelectTrigger className="h-8 text-sm bg-white" data-testid="critical-payment-frequency">
                  <SelectValue placeholder="Select frequency..." />
                </SelectTrigger>
                <SelectContent>
                  {["Daily", "Weekly", "Fortnightly", "Monthly"].map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Payment amount ({curSym}) <span className="text-red-500">*</span></label>
              <Input
                type="number"
                step="0.01"
                value={critical?.payment_amount || ""}
                onChange={(e) => setCriticalField("payment_amount", e.target.value)}
                placeholder="0.00"
                className="h-8 text-sm bg-white"
                disabled={isFunded}
                data-testid="critical-payment-amount"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Loan start date <span className="text-red-500">*</span></label>
              <Input
                type="date"
                value={critical?.start_date || ""}
                onChange={(e) => setCriticalField("start_date", e.target.value)}
                className="h-8 text-sm bg-white"
                disabled={isFunded}
                data-testid="critical-start-date"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Nominal amount received ({curSym})</label>
              <Input
                type="number"
                step="0.01"
                value={critical?.nominal_amount || ""}
                onChange={(e) => setCriticalField("nominal_amount", e.target.value)}
                placeholder={application.test_payment_confirmed ? "Already confirmed" : "Optional — blank if emailed in"}
                className="h-8 text-sm bg-white"
                disabled={isFunded || application.test_payment_confirmed}
                data-testid="critical-nominal-amount"
              />
              <p className="text-[10px] text-muted-foreground">
                {application.test_payment_confirmed
                  ? "Nominal payment already confirmed separately ✓"
                  : "Optional here — but disbursement stays locked until it's confirmed (here or via the workflow page)."}
              </p>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Call Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any special items discussed, customer concerns, etc."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
            rows={2}
            disabled={isFunded}
            data-testid="prefunding-notes"
          />
        </div>

        {/* Recording upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Call Recording (optional)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".mp3,.wav,.m4a,.mp4,.ogg,.webm"
              onChange={(e) => {
                if (e.target.files?.[0]) onUploadRecording(e.target.files[0]);
              }}
              className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              disabled={isFunded || uploadingRecording}
              data-testid="prefunding-recording-input"
            />
            {uploadingRecording && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
            {recording && (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                {recording}
              </span>
            )}
          </div>
        </div>

        {identityFailed && (
          <div className="p-2.5 bg-rose-50 border border-rose-300 rounded-lg text-sm text-rose-800 flex items-center gap-2" data-testid="prefunding-identity-fail-banner">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            <span>A Directors identity answer does not match the contract on file. Re-confirm it with the customer — submitting now records a FAILED call and keeps disbursement locked.</span>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onSaveDraft}
            disabled={isFunded || processing === "prefunding_call"}
            className="flex-1"
            data-testid="save-prefunding-draft-btn"
            title="Save your progress (e.g. call dropped) — resume the call later from where you left off"
          >
            <Save className="w-4 h-4 mr-2" />
            Save &amp; finish later
          </Button>
          <Button
            onClick={onComplete}
            disabled={processing === "prefunding_call" || isFunded || requiredUnanswered || criticalIncomplete}
            className="flex-[2] bg-blue-600 hover:bg-blue-700"
            data-testid="complete-prefunding-call-btn"
          >
            {processing === "prefunding_call" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Phone className="w-4 h-4 mr-2" />
            )}
            {criticalIncomplete ? "Complete Critical Info to Submit" : "Complete Pre-Funding Call"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
